import { Box, Stack, Typography, useTheme } from '@mui/material';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useEffect, useState } from 'react';

import { PoweredByLuminary } from '@/components/brand/PoweredByLuminary/PoweredByLuminary';
import { Loader } from '@/components/progress/Loader/Loader';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { SideDrawer } from '@/modules/common/SideDrawer';
import { ContextualHelpContext } from '@/modules/content/components/ContextualHelpContext';
import { COLORS } from '@/styles/tokens/colors';
import * as diagnostics from '@/utils/diagnostics';

export interface SidebarConfigShape {
  hideFooter: boolean;
}

interface ContextualHelpContent {
  id: string;
  heading: JSX.Element;
  content: JSX.Element;
  config?: SidebarConfigShape;
}

export function ContextualHelpDrawer() {
  const [contextualHelpContent, setContextualHelpContent] =
    useState<ContextualHelpContent | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const contextualHelpCtx = useContext(ContextualHelpContext);
  if (!contextualHelpCtx) {
    throw new Error(
      'ContextualHelpDrawer must be used inside a ContextualHelpProvider.'
    );
  }

  const fetchAndSetContextualHelpContent = async (contextualHelpId: string) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const Content = dynamic(
        () =>
          import(
            /* @vite-ignore */ `../sidebarContent/${contextualHelpId}/content`
          ).then((module) => module.Content),
        { ssr: false }
      );

      const Heading = dynamic(
        () =>
          import(
            /* @vite-ignore */ `../sidebarContent/${contextualHelpId}/content`
          ).then((module) => module.Heading),
        { ssr: false }
      );

      const maybeConfig = (await import(
        /* @vite-ignore */ `../sidebarContent/${contextualHelpId}/content`
      )) as {
        SidebarConfig?: SidebarConfigShape;
      };

      setContextualHelpContent({
        heading: <Heading />,
        content: <Content />,
        config: maybeConfig.SidebarConfig,
        id: contextualHelpId,
      });
      setIsLoading(false);
    } catch (err) {
      diagnostics.error('Failed to fetch contextual help', err as Error);
      setIsLoading(false);
      setContextualHelpContent(null);
      setErrorMessage(
        `Sorry, we weren't able to fetch this content! Please close this and try again.`
      );
    }
  };

  const handleClose = () => {
    contextualHelpCtx.setContextualHelp(null);
  };

  useEffect(() => {
    if (contextualHelpContent?.id === contextualHelpCtx.contextualHelpId) {
      return;
    }

    if (contextualHelpCtx.contextualHelpId === null) {
      return setContextualHelpContent(null);
    }

    void fetchAndSetContextualHelpContent(contextualHelpCtx.contextualHelpId);
  }, [
    setContextualHelpContent,
    contextualHelpContent,
    contextualHelpCtx.contextualHelpId,
  ]);

  const getDialogHeading = useCallback(() => {
    if (isLoading) return 'One moment...';
    if (errorMessage) return 'Help content';
    return contextualHelpContent?.heading;
  }, [isLoading, contextualHelpContent?.heading, errorMessage]);

  const getDialogBody = useCallback(() => {
    if (isLoading)
      return (
        <Loader
          boxProps={{
            sx: {
              textAlign: 'center',
              my: 3,
            },
          }}
        />
      );
    if (errorMessage) return errorMessage;
    return contextualHelpContent?.content;
  }, [isLoading, contextualHelpContent?.content, errorMessage]);

  // Negative margins cancel out SideDrawer's footer wrapper padding (px: 2, py: 1.5)
  // so the disclaimer renders edge-to-edge with its own styling (shadow, bgcolor)
  const footer = contextualHelpContent?.config?.hideFooter ? undefined : (
    <Box mx={-2} my={-1.5}>
      <ContextualHelpDisclaimerFooter />
    </Box>
  );

  return (
    <TextEditorProvider theme={'contextContent'}>
      <SideDrawer
        isOpen={contextualHelpCtx.contextualHelpId !== null}
        onClose={handleClose}
        heading={getDialogHeading()}
        bodyPropsSx={{ p: 2 }}
        body={<Box>{getDialogBody()}</Box>}
        footer={footer}
      />
    </TextEditorProvider>
  );
}

function ContextualHelpDisclaimerFooter() {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      height={60}
      p={1}
      boxShadow={theme.palette.shadows.drawerShadowInset}
      bgcolor={COLORS.GRAY[50]}
    >
      <Stack maxWidth={100} alignItems="center">
        <PoweredByLuminary textVariant="dark" />
      </Stack>
      <Box maxWidth="70%">
        <Typography variant="body-xs" textAlign="right">
          Luminary does not provide legal or tax advice. Consult with your legal
          and tax professionals prior to making any estate planning decisions.{' '}
        </Typography>
      </Box>
    </Stack>
  );
}
